import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AgilityRiskService } from '../../../../../providers/agility-risk.service';
import { CustomField, CustomFieldConstants, CustomFieldDropdownChoice } from '../../../../../models/custom-field.interface';
import { AssessmentMemberData } from '../../../../../models/assessment-member-data';
import * as _ from 'lodash';
import { CustomValue } from '../../../../../models/custom-value';
import { TranslateService } from '@ngx-translate/core';
import { AgilityRiskMessageService } from '../../../../../providers/agility-risk-message.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'lib-risk-assessment-details-custom-fields-modal',
  templateUrl: './risk-assessment-details-custom-fields-modal.component.html',
  styleUrls: ['./risk-assessment-details-custom-fields-modal.component.scss']
})
export class RiskAssessmentDetailsCustomFieldsModalComponent implements OnInit {
  public id: any;
  public customFields: Array<CustomField> = [];
  public activeCustomFieldId: any;
  public assessmentMemberData: AssessmentMemberData[] = [];
  public assessmentMemberDataOriginal: AssessmentMemberData[] = []; // keep a copy of original
  public customFieldConstants = CustomFieldConstants;
  public saving: boolean;
  public loading: boolean;
  public canEdit: boolean = false;

  constructor(public dynamicDialogRef: DynamicDialogRef,
              public dynamicDialogConfig: DynamicDialogConfig,
              private agilityRiskService: AgilityRiskService,
              public messageService: AgilityRiskMessageService,
              public translateService: TranslateService,
              public datePipe: DatePipe) {
    this.id = this.dynamicDialogConfig?.data?.id;
    this.canEdit = this.dynamicDialogConfig?.data?.canEdit === true;
  }

  ngOnInit(): void {
    this.getCustomFields();
    this.getRiskAssessmentRiskCustomValues();
  }

  getRiskAssessmentRiskCustomValues() {
    this.loading = true;
    this.agilityRiskService.getRiskAssessmentRiskCustomValues(this.id).subscribe(
      (assessmentMemberData: AssessmentMemberData[]) => {
        this.assessmentMemberData = assessmentMemberData;
        this.loading = false;

        // format the value correctly or else it some fields will break
        this.assessmentMemberData.forEach((amd: AssessmentMemberData) => {
          amd.memberCustomValues.forEach((cv: CustomValue<any>) => {
            switch (cv.type.toLowerCase()) {
              case this.customFieldConstants.date.toLowerCase():
                cv.value = cv.value ? new Date(cv.value.replace(/-0(\d{1,2})-/, '-$1-')) : null;
                break;
              case this.customFieldConstants.multiselect.toLowerCase():
                cv.value = cv.value ? cv.value.map((v: CustomFieldDropdownChoice) => v.name) : null;
                break;
              case this.customFieldConstants.picklist.toLowerCase():
                cv.value = cv.value ? cv.value.name : null;
                break;
              case this.customFieldConstants.radio.toLowerCase():
                cv.value = cv.value ? cv.value.id : null;
                break;
            }
          });
        });

        // after formatting correclty, clone to keep a bakup
        this.assessmentMemberDataOriginal = _.cloneDeep(this.assessmentMemberData);
      },
      (error: any) => {
        this.messageService.error(this.translateService.instant('common.error'));
      }
    );
  }

  getCustomFields(): void {
    this.agilityRiskService.getCustomFields('RISK')
      .subscribe((customFields: Array<CustomField>) => {
        this.customFields = customFields;
      });
  }

  setActiveCustomField(customField: CustomField) {
    this.activeCustomFieldId = customField.id;

    // reset the answers so only the active ones can be updated
    this.assessmentMemberData = _.cloneDeep(this.assessmentMemberDataOriginal);
  }

  getCustomField(id: any): CustomField {
    return this.customFields.find((cf: CustomField) => Number(cf.id) === Number(id));
  }

  getStep(id: string): string {
    // TODO: get the correct step from decimals field
    return '0.01';
  }

  close() {
    this.dynamicDialogRef.close();
  }

  saveChanges() {
    this.saving = true;
    // make a clone so can manipulate before saving
    const save: AssessmentMemberData[] = _.cloneDeep(this.assessmentMemberData);

    // remove customValues that is formula type, otherwise will error
    save.forEach((amd: AssessmentMemberData) => {
      amd.memberCustomValues = amd.memberCustomValues.filter((cv: CustomValue<any>) => cv.type !== this.customFieldConstants.formula);

      // mold the value of each type to be what server expects
      // NOTE: using here == to avoid strict typing. can either be number or string type.
      amd.memberCustomValues.forEach((ma: CustomValue<any>) => {
        switch (ma.type.toLowerCase()) {
          case this.customFieldConstants.multiselect.toLowerCase():
            const newValue: Array<any> = [];
            ma.value.forEach((v: any) => {
              newValue.push(this.getCustomField(ma.field.id).choices.find((c: CustomFieldDropdownChoice) => c.name === v));
            });
            ma.value = newValue;
            break;
          case this.customFieldConstants.date.toLowerCase():
            ma.value = this.datePipe.transform(ma.value, 'yyyy-MM-dd');
            break;
          case this.customFieldConstants.picklist.toLowerCase():
            ma.value = this.getCustomField(ma.field.id).choices.find((c: CustomFieldDropdownChoice) => c.name == ma.value) || ma.value;
            break;
          case this.customFieldConstants.radio.toLowerCase():
            ma.value = this.getCustomField(ma.field.id).choices.find((c: CustomFieldDropdownChoice) => c.id == ma.value) || ma.value;
            break;
        }
      });
    });

    this.agilityRiskService.putRiskAssessmentRiskCustomValues(this.id, save).subscribe(
      () => {
        this.messageService.success(this.translateService.instant('common.saved'));
        // update the original to the new changes
        this.assessmentMemberDataOriginal = _.cloneDeep(this.assessmentMemberData);
      },
      () => {
        this.saving = false;
      },
      () => {
        this.saving = false;
      }
    );
  }

  memberHasCustomValue(amd: AssessmentMemberData): boolean {
    return !!(amd.memberCustomValues.find((mcv: CustomValue<any>) => mcv.field.id === this.activeCustomFieldId));
  }
}
